import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import {
  DEFAULT_THEME,
  isTheme,
  THEME_META_COLORS,
  THEME_STORAGE_KEY,
} from "./theme.constants";

const ThemeContext = createContext(null);

const readStoredTheme = () => {
  if (typeof window === "undefined") {
    return DEFAULT_THEME;
  }

  try {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(storedTheme) ? storedTheme : DEFAULT_THEME;
  } catch (_) {
    return DEFAULT_THEME;
  }
};

export function AppThemeProvider({children}) {
  const [theme, setThemeState] = useState(readStoredTheme);

  const setTheme = useCallback((nextTheme) => {
    if (isTheme(nextTheme)) {
      setThemeState(nextTheme);
    }
  }, []);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    root.style.colorScheme = theme === "light" ? "light" : "dark";

    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) {
      themeColor.setAttribute("content", THEME_META_COLORS[theme]);
    }

    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (_) {
      // The theme still works when storage is unavailable.
    }
  }, [theme]);

  const value = useMemo(() => ({theme, setTheme}), [theme, setTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useAppTheme() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useAppTheme must be used inside AppThemeProvider");
  }

  return context;
}
